import { styled } from '@mui/material/styles';
import { Box, Button, Checkbox, IconButton, TextField, Typography } from '@mui/material';

// 로그인 페이지에서만 사용하는 색상.
const COLORS = {
  primary: '#4AB3BC',
  border: '#D9D9D9',
  textDark: '#000',
  textMuted: '#737373',
  textCheckbox: '#5A5A5A',
  iconMuted: '#9A9A9A',
};

// ============================================
// Layout
// ============================================
export const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #66828E 0%, #516A6F 35%, #516A6F 65%, #5F838F 100%)',
  overflow: 'hidden',
  boxSizing: 'border-box',
  padding: '20px',
  [theme.breakpoints.up('sm')]: {
    padding: '40px',
  },
}));

// 좌측 이미지 + 우측 폼이 들어가는 가운데 카드.
export const LoginCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: '1060px',
  height: 'auto',
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.18)',
  backgroundColor: '#ffffff',
  [theme.breakpoints.up('md')]: {
    height: '640px',
  },
}));

// 좌측 이미지 영역 — md 이상에서만 노출.
export const LeftImagePane = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'none',
  flexShrink: 0,
  [theme.breakpoints.up('md')]: {
    display: 'block',
    width: '40%',
  },
}));

export const LeftImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const RightPane = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    width: '60%',
  },
}));

// form 태그로 만들어 Enter 키 자동 제출을 활용 (window keydown 리스너 대체).
export const FormFrame = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: '100%',
  padding: '40px 30px',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '490px',
    padding: '60px 50px 64px',
  },
}));

// ============================================
// Brand intro
// ============================================
export const BrandIntro = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '60px',
});

export const BrandTagline = styled(Typography)({
  fontSize: '24px',
  lineHeight: '20px',
  color: COLORS.primary,
  fontWeight: 'regular',
  fontFamily: "'THEFACESHOP' !important",
  marginBottom: '8px',
});

export const BrandTitleRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: '8px',
});

// bold prop으로 BODY/SWITCH 두께 차이 처리.
export const BrandTitle = styled(Typography, {
  shouldForwardProp: prop => prop !== 'bold',
})(({ bold }) => ({
  fontSize: '32px',
  lineHeight: '40px',
  color: COLORS.primary,
  fontWeight: bold ? 1000 : 1,
  letterSpacing: '-0.5px',
}));

// ============================================
// Fields
// ============================================
export const FieldsGroup = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  marginBottom: '24px',
});

export const FieldBlock = styled(Box)({
  width: '100%',
});

export const FieldLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: COLORS.textDark,
  marginBottom: '8px',
});

// 아이디/비밀번호 공통 입력 스타일.
// MUI 내부 스타일이 cascade 순서에 따라 덮을 수 있어 input 폰트 크기는 !important로 강제.
export const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: '48px',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: COLORS.border,
    },
    '&:hover fieldset': {
      borderColor: COLORS.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.primary,
      borderWidth: '2px',
    },
  },
  '& input': {
    fontSize: '16px !important',
    color: COLORS.textDark,
  },
});

export const PasswordToggleIconButton = styled(IconButton)({
  color: COLORS.iconMuted,
});

// ============================================
// Save ID row
// ============================================
export const SaveIdRow = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '40px',
});

export const SaveIdCheckbox = styled(Checkbox)({
  color: COLORS.border,
  '&.Mui-checked': {
    color: COLORS.primary,
  },
  padding: 0,
  marginRight: '8px',
});

export const SaveIdText = styled(Typography)({
  color: COLORS.textCheckbox,
  fontSize: '16px',
  fontWeight: 400,
});

// ============================================
// Submit
// ============================================
export const SubmitButton = styled(Button)({
  height: '48px',
  borderRadius: '8px',
  fontSize: '22px',
  fontWeight: 600,
  color: COLORS.primary,
  borderColor: COLORS.primary,
  backgroundColor: '#FFFFFF',
  marginBottom: '60px',
  '&:hover': {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,

    color: '#FFFFFF',
  },
  '&.Mui-disabled': {
    borderColor: COLORS.border,
    color: COLORS.border,
  },
});

// ============================================
// Footer links
// ============================================
export const FooterLinks = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
});

export const FooterLink = styled(Typography)({
  fontSize: '14px',
  color: COLORS.textMuted,
  cursor: 'pointer',
  '&:hover': {
    color: COLORS.primary,
  },
});

export const FooterDivider = styled(Typography)({
  fontSize: '12px',
  color: COLORS.border,
});
